/**
 * Registry mapping a `toolId` to its replay handler.
 *
 * A tool becomes truly replayable (rather than relying on the mesh-snapshot
 * fallback) once it registers a handler here. Keep handlers pure and
 * deterministic: given the same input mesh + params they must produce the same
 * geometry, with no dependency on live React/scene state beyond what is passed in.
 *
 * @typedef {import('./operationDescriptor').OperationDescriptor} OperationDescriptor
 *
 * @typedef {Object} OperationHandler
 * @property {(ctx: ReplayContext, params: Record<string, unknown>) => (Promise<import('three').Mesh> | import('three').Mesh)} replay
 *   Apply the operation to `ctx.mesh` and return the resulting mesh (may be the
 *   same instance mutated in place, or a new one). Throwing aborts replay and
 *   triggers the snapshot fallback for the owning step.
 * @property {boolean} [deterministic=true]  Set false for ops that cannot be
 *   faithfully reproduced (interactive/ML/random); replay will prefer the
 *   per-step snapshot for steps containing such ops.
 *
 * @typedef {Object} ReplayContext
 * @property {import('three').Mesh} mesh   Current working mesh (result of prior ops).
 * @property {import('three').Scene} [scene]
 * @property {AbortSignal} [signal]
 */

/** @type {Map<string, OperationHandler>} */
const registry = new Map();

/**
 * @param {string} toolId
 * @param {OperationHandler} handler
 */
export function registerOperation(toolId, handler) {
  if (!toolId || typeof handler?.replay !== 'function') {
    console.warn('[OperationRegistry] ignoring invalid registration for', toolId);
    return;
  }
  registry.set(toolId, { deterministic: true, ...handler });
}

/**
 * @param {string} toolId
 * @returns {OperationHandler | null}
 */
export function getOperationHandler(toolId) {
  return registry.get(toolId) ?? null;
}

/** True when every op in the list has a deterministic replay handler. */
export function canReplayAll(operations) {
  return operations.every((op) => {
    const h = registry.get(op.toolId);
    return h && h.deterministic !== false;
  });
}

export function hasOperationHandler(toolId) {
  return registry.has(toolId);
}
